import React from "react";
import { useTranslation } from "react-i18next";
import { BadgeDollarSign, Building, ChevronDown, Sparkles, User, X } from "lucide-react";

import CallRecordingPicker from "./CallRecordingPicker";
import CrmChatRecordPicker from "./CrmChatRecordPicker";
import type { ChatContextBarProps, CrmRecordType } from "../types/chat.types";

const RECORD_ICONS: Record<CrmRecordType, React.ReactNode> = {
  COMPANY: <Building className="h-3.5 w-3.5" />,
  CONTACT: <User className="h-3.5 w-3.5" />,
  SALE: <BadgeDollarSign className="h-3.5 w-3.5" />,
};

const RECORD_LABELS: Record<CrmRecordType, string> = {
  COMPANY: "Company",
  CONTACT: "Contact",
  SALE: "Deal",
};

const ChatContextBar: React.FC<ChatContextBarProps> = ({
  record,
  promptType,
  promptName,
  onChangeRecord,
  onClearRecord,
  onClearPrompt,
  workspaceId,
  onSelectRecord,
  promptPickerSlot,
  disabled = false,
  callRecordings,
  selectedCallRecordingId,
  onSelectCallRecording,
  callRecordingsLoading = false,
  requiresCallRecordingWithTranscript = false,
}) => {
  const { t } = useTranslation();
  const showCallPicker = Boolean(
    record &&
      promptType &&
      requiresCallRecordingWithTranscript
  );

  return (
    <div className="flex flex-col gap-1.5 border-b border-gray-200 bg-white px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        {t("Context")}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {workspaceId && onSelectRecord ? (
          <CrmChatRecordPicker
            workspaceId={workspaceId}
            value={record ?? null}
            onChange={(next) => {
              if (next) onSelectRecord(next);
              else onClearRecord?.();
            }}
            disabled={disabled}
          />
        ) : record ? (
          <RecordChip
            icon={RECORD_ICONS[record.recordType]}
            label={record.displayName}
            sublabel={record.subtitle || RECORD_LABELS[record.recordType]}
            onChange={onChangeRecord}
            onClear={onClearRecord}
            disabled={disabled}
            changeLabel={t("Change") as string}
            clearLabel={t("Remove record") as string}
          />
        ) : (
          <button
            type="button"
            onClick={onChangeRecord}
            disabled={disabled || !onChangeRecord}
            className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 px-3 py-1 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Building className="h-3.5 w-3.5" />
            {t("Choose a CRM record")}
          </button>
        )}

        {promptType ? (
          <PromptChip name={promptName ?? promptType} onClear={onClearPrompt} disabled={disabled} />
        ) : promptPickerSlot ? (
          promptPickerSlot
        ) : (
          <span className="text-xs text-gray-400">{t("No prompt selected")}</span>
        )}
      </div>

      {showCallPicker && onSelectCallRecording ? (
        <CallRecordingPicker
          recordings={callRecordings ?? []}
          selectedId={selectedCallRecordingId ?? null}
          onSelect={onSelectCallRecording}
          record={record}
          loading={callRecordingsLoading}
          disabled={disabled}
        />
      ) : null}
    </div>
  );
};

interface RecordChipProps {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  onChange?: () => void;
  onClear?: () => void;
  disabled?: boolean;
  changeLabel: string;
  clearLabel: string;
}

const RecordChip: React.FC<RecordChipProps> = ({
  icon,
  label,
  sublabel,
  onChange,
  onClear,
  disabled,
  changeLabel,
  clearLabel,
}) => (
  <div className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 py-1 pl-2 pr-1 text-xs text-gray-800">
    <span className="text-gray-500" aria-hidden="true">
      {icon}
    </span>
    <span className="font-medium">{label}</span>
    {sublabel ? <span className="text-gray-500">· {sublabel}</span> : null}
    {onChange ? (
      <button
        type="button"
        onClick={onChange}
        disabled={disabled}
        className="ml-1 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {changeLabel}
        <ChevronDown className="h-3 w-3" />
      </button>
    ) : null}
    {onClear ? (
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={clearLabel}
      >
        <X className="h-3 w-3" />
      </button>
    ) : null}
  </div>
);

interface PromptChipProps {
  name: string;
  onClear?: () => void;
  disabled?: boolean;
}

const PromptChip: React.FC<PromptChipProps> = ({ name, onClear, disabled }) => {
  const { t } = useTranslation();
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-blue-50 py-1 pl-2 pr-1 text-xs text-blue-700">
      <Sparkles className="h-3 w-3" />
      <span className="font-medium">{name}</span>
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-blue-600 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={t("Remove prompt")}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );
};

export default ChatContextBar;
